<template>
  <div class="gw-wrapper">
    <div v-if="isRedirectingWallet" class="gw-page-loader">
      <div class="gw-loader-card">
        <div class="gw-spinner"></div>
        <div class="gw-loader-title">Түр хүлээнэ үү...</div>
        <div class="gw-loader-desc">Google Wallet pass үүсгэж байна.</div>
      </div>
    </div>

    <div class="gw-bg-shape gw-bg-shape-left"></div>
    <div class="gw-bg-shape gw-bg-shape-right"></div>

    <div class="gw-card gw-card-signup">
      <div class="gw-logo-wrap">
        <img src="@/assets/logo.png" alt="Номин Картын Төв" class="gw-logo" />
      </div>

      <div class="gw-badge">
        <span class="gw-wallet-icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" width="18" height="18">
            <path
              d="M4 7.5C4 6.1 5.1 5 6.5 5H18c1.1 0 2 .9 2 2v1.2h-2V7H6.5c-.3 0-.5.2-.5.5S6.2 8 6.5 8H20c1.1 0 2 .9 2 2v7c0 1.1-.9 2-2 2H5c-1.7 0-3-1.3-3-3V7.5h2Z"
              fill="currentColor"
              opacity=".18"
            />
            <path
              d="M5 8h15c1.1 0 2 .9 2 2v7c0 1.1-.9 2-2 2H5c-1.7 0-3-1.3-3-3V7.5C2 5 4 3 6.5 3H18c1.1 0 2 .9 2 2v2h-2V5H6.5C5.1 5 4 6.1 4 7.5V16c0 .6.4 1 1 1h15v-7H5V8Zm12 5.5c0-.8.7-1.5 1.5-1.5S20 12.7 20 13.5 19.3 15 18.5 15 17 14.3 17 13.5Z"
              fill="currentColor"
            />
          </svg>
        </span>
        Google Wallet холболт
      </div>

      <h1 class="gw-title">
        Google Wallet-д картаа нэмэхийн<br />
        тулд бүртгэлээ баталгаажуулна уу.
      </h1>

      <div v-if="hasUserProfileParam" class="gw-info-box">
        <span class="gw-info-icon">i</span>
        <span>
          Доорх мэдээлэл нь Google Wallet-ээс ирсэн бөгөөд автоматаар
          бөглөгдсөн. Та шаардлагатай бол засварлаж болно.
        </span>
      </div>

      <b-alert variant="danger" :show="!!errorMessage" class="gw-alert">
        {{ errorMessage }}
      </b-alert>

      <b-form class="gw-form" @submit.prevent="onSubmit">
        <div class="gw-grid">
          <div class="gw-form-group">
            <label for="gw-last-name">Овог</label>
            <input
              id="gw-last-name"
              v-model.trim="form.lastName"
              type="text"
              class="gw-input gw-prefilled"
              autocomplete="family-name"
            />
          </div>

          <div class="gw-form-group">
            <label for="gw-first-name">Нэр</label>
            <input
              id="gw-first-name"
              v-model.trim="form.firstName"
              type="text"
              class="gw-input gw-prefilled"
              autocomplete="given-name"
            />
          </div>
        </div>

        <div class="gw-grid">
          <div class="gw-form-group">
            <label for="gw-registry-number">Регистрийн дугаар</label>
            <input
              id="gw-registry-number"
              v-model.trim="form.registryNumber"
              type="text"
              class="gw-input"
              placeholder="АА00000000"
              autocomplete="off"
              @input="
                form.registryNumber = normalizeRegistryNumber(
                  form.registryNumber,
                )
              "
            />
          </div>

          <div class="gw-form-group">
            <label for="gw-email">И-Мэйл</label>
            <input
              id="gw-email"
              v-model.trim="form.email"
              type="email"
              class="gw-input gw-prefilled"
              autocomplete="email"
            />
          </div>
        </div>

        <div class="gw-grid">
          <div class="gw-form-group">
            <label for="gw-phone">Утасны дугаар</label>
            <div class="gw-inline-action">
              <input
                id="gw-phone"
                v-model.trim="form.phone"
                type="text"
                class="gw-input gw-prefilled"
                placeholder="99112233"
                autocomplete="tel"
              />
              <button
                type="button"
                class="gw-small-btn"
                :disabled="isSendingCode || isPhoneVerified || isRedirectingWallet"
                @click="sendPhoneCode"
              >
                {{ isSendingCode ? "Илгээж байна..." : "Код илгээх" }}
              </button>
            </div>
          </div>

          <div class="gw-form-group">
            <label for="gw-otp-code">Баталгаажуулах код</label>
            <div class="gw-inline-action">
              <input
                id="gw-otp-code"
                v-model.trim="otpCode"
                type="text"
                class="gw-input"
                placeholder="Код"
                autocomplete="one-time-code"
                :disabled="isRedirectingWallet"
              />
              <button
                type="button"
                class="gw-small-btn gw-confirm-btn"
                :disabled="isCheckingCode || isPhoneVerified || isRedirectingWallet"
                @click="confirmPhoneCode"
              >
                {{ isCheckingCode ? "Шалгаж байна..." : "Баталгаажуулах" }}
              </button>
            </div>
          </div>
        </div>

        <div
          class="gw-otp-status"
          :class="{
            success: isPhoneVerified,
            muted: !otpMessage && !isPhoneVerified,
          }"
        >
          {{ otpMessage || "Утасны дугаараа баталгаажуулна уу." }}
        </div>

        <button
          type="submit"
          class="gw-primary-btn"
          :disabled="isLoading || isRedirectingWallet"
        >
          <span>{{
            isLoading || isRedirectingWallet
              ? "Түр хүлээнэ үү..."
              : "Бүртгүүлээд Google Wallet-д нэмэх"
          }}</span>
          <span class="gw-btn-arrow">→</span>
        </button>
      </b-form>

      <div class="gw-login-link">
        <router-link
          :to="{ path: '/google-wallet/sign-in', query: $route.query }"
        >
          Бүртгэлтэй бол нэвтрэх
        </router-link>
      </div>
    </div>
  </div>
</template>

<script>
const GOOGLE_WALLET_CREATE_PASS_URL = "loyalty/create/google/wallet/pass";

export default {
  name: "GoogleWalletSignUp",

  data() {
    return {
      form: {
        firstName: "",
        lastName: "",
        registryNumber: "",
        email: "",
        phone: "",
      },
      rawUserProfile: "",
      acceptedTerms: true,
      isLoading: false,
      errorMessage: "",

      isRedirectingWallet: false,

      otpCode: "",
      otpTokenId: "",
      otpMessage: "",
      isSendingCode: false,
      isCheckingCode: false,
      isPhoneVerified: false,
      verifiedPhoneNumber: "",
    };
  },
  computed: {
  hasUserProfileParam() {
    return !!(
      this.$route.query.userProfile ||
      this.$route.query.userprofile
    );
  },
},

  watch: {
    "form.phone"(value) {
      const currentPhone = this.normalizePhone(value);

      if (
        this.verifiedPhoneNumber &&
        currentPhone !== this.verifiedPhoneNumber
      ) {
        this.isPhoneVerified = false;
        this.verifiedPhoneNumber = "";
        this.otpMessage =
          "Утасны дугаар өөрчлөгдсөн тул дахин баталгаажуулна уу.";
      }
    },
  },

  methods: {
    loadUserProfile() {
      const token =
        this.$route.query.userProfile || this.$route.query.userprofile || "";

      this.rawUserProfile = token;

      if (!token) {
        return;
      }

      try {
        const profile = this.decodeUserProfile(token);

        this.form.firstName =
          profile.firstName ||
          profile.given_name ||
          profile.givenName ||
          this.form.firstName;

        this.form.lastName =
          profile.lastName ||
          profile.family_name ||
          profile.familyName ||
          this.form.lastName;

        this.form.email = profile.email || this.form.email;

        this.form.phone =
          profile.phone ||
          profile.phoneNumber ||
          profile.mobile ||
          this.form.phone;

        this.form.registryNumber =
          profile.registryNumber ||
          profile.registerNo ||
          profile.registryNo ||
          profile.regNo ||
          this.form.registryNumber;

        this.form.registryNumber = this.normalizeRegistryNumber(
          this.form.registryNumber,
        );

        this.otpTokenId =
          profile.tokenId ||
          profile.token ||
          profile.userProfileToken ||
          this.$route.query.tokenId ||
          this.rawUserProfile ||
          "";
      } catch (e) {
        this.errorMessage =
          "Google Wallet-с ирсэн userProfile уншихад алдаа гарлаа.";
      }
    },

    decodeUserProfile(token) {
      const value = String(token || "");
      const parts = value.split(".");
      const payload = parts.length >= 2 ? parts[1] : value;
      const json = this.base64UrlDecode(payload);

      return JSON.parse(json);
    },

    base64UrlDecode(value) {
      let base64 = String(value || "")
        .replace(/-/g, "+")
        .replace(/_/g, "/");

      while (base64.length % 4) {
        base64 += "=";
      }

      const binary = window.atob(base64);
      const bytes = [];

      for (let i = 0; i < binary.length; i += 1) {
        bytes.push("%" + ("00" + binary.charCodeAt(i).toString(16)).slice(-2));
      }

      return decodeURIComponent(bytes.join(""));
    },

    normalizePhone(phone) {
      return String(phone || "")
        .replace(/\s/g, "")
        .replace(/-/g, "")
        .replace(/^\+976/, "");
    },

    normalizeRegistryNumber(value) {
      return String(value || "")
        .replace(/\s/g, "")
        .toUpperCase();
    },

    getOtpTokenId() {
      return (
        this.otpTokenId ||
        this.$route.query.tokenId ||
        this.$route.query.tokenid ||
        this.rawUserProfile ||
        ""
      );
    },

    async sendPhoneCode() {
      this.errorMessage = "";
      this.otpMessage = "";

      const phoneNumber = this.normalizePhone(this.form.phone);

      if (!phoneNumber) {
        this.errorMessage = "Утасны дугаараа оруулна уу.";
        return;
      }

      if (!/^\d{8}$/.test(phoneNumber)) {
        this.errorMessage = "Утасны дугаар 8 оронтой байх ёстой.";
        return;
      }

      this.isSendingCode = true;

      if (this.$insProgress) {
        this.$insProgress.start();
      }

      try {
        const response = await this.$bonusApi.post(
          "/otp/send",
          {
            referenceId: null,
            purpose: "LOGIN",
            channel: "SMS",
            receiver: phoneNumber,
            ipAddress: "1.1.1.1",
            systemId: "GoogleWallet",
          },
          {
            headers: {
              "Content-Type": "application/json",
              "Accept-Language": "mn",
            },
          },
        );

        const data = response.data || {};

        if (!data.success) {
          this.errorMessage = data.message || "OTP код илгээхэд алдаа гарлаа.";
          return;
        }

        this.otpMessage = data.message || "Баталгаажуулах код илгээгдлээ.";
      } catch (error) {
        this.errorMessage = this.getErrorMessage(error);
      } finally {
        this.isSendingCode = false;

        if (this.$insProgress) {
          this.$insProgress.finish();
        }
      }
    },

    async confirmPhoneCode() {
      this.errorMessage = "";
      this.otpMessage = "";

      const phoneNumber = this.normalizePhone(this.form.phone);
      const code = String(this.otpCode || "").replace(/\s/g, "");

      if (!phoneNumber) {
        this.errorMessage = "Утасны дугаараа оруулна уу.";
        return;
      }

      if (!code) {
        this.errorMessage = "Баталгаажуулах кодоо оруулна уу.";
        return;
      }

      this.isCheckingCode = true;

      if (this.$insProgress) {
        this.$insProgress.start();
      }

      try {
        const response = await this.$bonusApi.post(
          "/otp/verify",
          {
            referenceId: null,
            purpose: "LOGIN",
            channel: "SMS",
            receiver: phoneNumber,
            otpCode: code,
            systemId: "GoogleWallet",
          },
          {
            headers: {
              "Content-Type": "application/json",
              "Accept-Language": "mn",
            },
          },
        );

        const data = response.data || {};

        if (data.success === false) {
          this.errorMessage = data.message || "Баталгаажуулах код буруу байна.";
          return;
        }

        this.isPhoneVerified = true;
        this.verifiedPhoneNumber = phoneNumber;
        this.otpMessage = data.message || "Утасны дугаар баталгаажлаа.";
      } catch (error) {
        this.errorMessage = this.getErrorMessage(error);
      } finally {
        this.isCheckingCode = false;

        if (this.$insProgress) {
          this.$insProgress.finish();
        }
      }
    },

    validateForm() {
      this.form.registryNumber = this.normalizeRegistryNumber(
        this.form.registryNumber,
      );

      if (
        !this.form.lastName ||
        !this.form.firstName ||
        !this.form.registryNumber ||
        !this.form.email ||
        !this.form.phone
      ) {
        this.errorMessage =
          "Овог, нэр, регистр, и-мэйл болон утасны дугаарын мэдээлэл дутуу байна.";
        return false;
      }

      const registryRegex = /^[А-ЯЁӨҮ]{2}\d{8}$/u;

      if (!registryRegex.test(this.form.registryNumber)) {
        this.errorMessage = "Регистрийн дугаар 2 үсэг, 8 тооноос бүрдэх ёстой.";
        return false;
      }

      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

      if (!emailRegex.test(this.form.email)) {
        this.errorMessage = "И-мэйл хаяг буруу байна.";
        return false;
      }

      const phoneNumber = this.normalizePhone(this.form.phone);

      if (!/^\d{8}$/.test(phoneNumber)) {
        this.errorMessage = "Утасны дугаар 8 оронтой байх ёстой.";
        return false;
      }

      if (!this.isPhoneVerified || this.verifiedPhoneNumber !== phoneNumber) {
        this.errorMessage = "Утасны дугаараа баталгаажуулна уу.";
        return false;
      }

      return true;
    },

    async onSubmit() {
      this.errorMessage = "";

      if (!this.validateForm()) {
        return;
      }

      this.isLoading = true;

      if (this.$insProgress) {
        this.$insProgress.start();
      }

      try {
        const registerResult = await this.registerGoogleWalletUser();

        if (!registerResult) {
          return;
        }

        await this.createCardAndRedirect({
          cardId: this.normalizePhone(this.form.phone),
        });
      } finally {
        this.isLoading = false;

        if (this.$insProgress) {
          this.$insProgress.finish();
        }
      }
    },

    async registerGoogleWalletUser() {
      try {
        const response = await this.$bonusApi.post(
          "auth/create/card",
          {
            firstName: this.form.firstName,
            lastName: this.form.lastName,
            registryNumber: this.form.registryNumber,
            mail: this.form.email,
            phoneNumber: this.normalizePhone(this.form.phone),
            systemId: "BonusWeb-GoogleWallet",
          },
          {
            headers: {
              "Accept-Language": "en-US",
              "Content-Type": "application/json",
              "Access-Control-Allow-Origin": "*",
            },
          },
        );

        const data = response.data || {};

        if (data.success === false || data.status === 100) {
          this.errorMessage = data.message || "Бүртгэл үүсгэхэд алдаа гарлаа.";
          return null;
        }

        return data;
      } catch (error) {
        this.errorMessage = this.getErrorMessage(error);
        return null;
      }
    },

    async createCardAndRedirect(payload) {
      this.errorMessage = "";
      this.isRedirectingWallet = true;

      const requestBody = this.buildCreateWalletPassRequest(payload);

      if (!requestBody) {
        this.isRedirectingWallet = false;
        return;
      }

      try {
        const response = await this.$bonusApi.post(
          GOOGLE_WALLET_CREATE_PASS_URL,
          requestBody,
          {
            headers: {
              "Content-Type": "application/json",
              "Accept-Language": "mn",
            },
          },
        );

        const data = response.data || {};

        if (data.success === false) {
          this.errorMessage =
            data.message || "Google Wallet pass үүсгэхэд алдаа гарлаа.";
          this.isRedirectingWallet = false;
          return;
        }

        const walletLink = this.extractWalletLink(data);

        if (!walletLink) {
          this.errorMessage =
            data.message || "Google Wallet walletLink ирсэнгүй.";
          this.isRedirectingWallet = false;
          return;
        }

        window.location.href = walletLink;
      } catch (error) {
        this.errorMessage = this.getErrorMessage(error);
        this.isRedirectingWallet = false;
      }
    },

    buildCreateWalletPassRequest(payload) {
      const cardId =
        payload.cardId ||
        payload.cardID ||
        payload.cardNumber ||
        payload.cardNo ||
        payload.id ||
        "";

      if (!cardId) {
        this.errorMessage =
          "Google Wallet pass үүсгэх cardId мэдээлэл дутуу байна.";
        return null;
      }

      return {
        cardId: cardId,
      };
    },

    extractWalletLink(data) {
      if (!data) {
        return "";
      }

      if (typeof data === "string") {
        return data.indexOf("http") === 0 ? data : "";
      }

      return (
        data.walletLink ||
        data.walletlink ||
        data.url ||
        data.link ||
        (data.data &&
          (data.data.walletLink ||
            data.data.walletlink ||
            data.data.url ||
            data.data.link)) ||
        ""
      );
    },

    getErrorMessage(error) {
      if (error && error.response && error.response.data) {
        const data = error.response.data;

        return data.message || data.error || "Бүртгэл үүсгэхэд алдаа гарлаа.";
      }

      return (error && error.message) || "Бүртгэл үүсгэхэд алдаа гарлаа.";
    },
  },

  created() {
    this.loadUserProfile();
  },
};
</script>

<style scoped>
.gw-wrapper {
  background-image: linear-gradient(
    to bottom,
    #5192d4,
    #4984c6,
    #4275b8,
    #3b68a9,
    #345a9b
  );
  min-height: 100vh;
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  overflow: hidden;
  padding: 20px;
  font-family: "Roboto", Arial, sans-serif;
}

.gw-bg-shape {
  display: none;
}

.gw-card {
  width: 520px;
  max-width: 520px;
  border-radius: 10px;
  box-shadow: 0px 25px 59px 0 rgba(0, 0, 0, 0.05);
  background-color: #ffffff;
  padding: 42px 48px 28px;
  position: relative;
  z-index: 1;
}

.gw-card-signup {
  max-width: 520px;
}

.gw-logo-wrap {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 18px;
}

.gw-logo {
  width: 250px;
  height: 32px;
  object-fit: contain;
}

.gw-badge {
  margin: 0 auto 16px;
  width: max-content;
  max-width: 100%;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 5px 12px;
  border-radius: 999px;
  color: #1660ab;
  background: #eef5fb;
  font-weight: 500;
  font-size: 11px;
}

.gw-wallet-icon {
  display: inline-flex;
  color: #1660ab;
}

.gw-wallet-icon svg {
  width: 14px;
  height: 14px;
}

.gw-title {
  margin: 0 0 18px;
  text-align: center;
  color: #202a3a;
  font-size: 16px;
  font-weight: 400;
  line-height: 1.45;
}

.gw-info-box {
  min-height: auto;
  border: 1px solid #c2d9f2;
  border-radius: 5px;
  background: #f4f9ff;
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 9px 12px;
  color: #61708a;
  font-size: 11px;
  line-height: 1.35;
  margin-bottom: 16px;
}

.gw-info-icon {
  width: 18px;
  height: 18px;
  flex: 0 0 18px;
  border: 1.5px solid #1660ab;
  border-radius: 50%;
  color: #1660ab;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-style: normal;
  font-size: 11px;
  line-height: 1;
}

.gw-alert {
  border-radius: 5px;
  font-size: 11px;
  padding: 8px 10px;
  margin-bottom: 12px;
}

.gw-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0 16px;
}

.gw-form-group {
  margin-bottom: 12px;
}

.gw-form-group label {
  width: 100%;
  height: auto;
  font-family: "Roboto", Arial, sans-serif;
  font-size: 11px;
  margin-bottom: 7px;
  font-weight: bold;
  font-style: normal;
  letter-spacing: 0.3px;
  text-align: left;
  color: #000000;
  text-transform: uppercase;
  display: block;
}

.gw-form-group small {
  font-weight: 400;
  color: #596579;
  text-transform: none;
  font-size: 10px;
}

.gw-input {
  height: 36px;
  border-radius: 5px;
  font-size: 13px;
  width: 100%;
  background-color: #f5f5f5;
  border: 1px solid #ced4da;
  padding: 6px 12px;
  outline: none;
  color: #273142;
}

.gw-prefilled {
  background: #f5f5f5;
  border-color: #ced4da;
}

.gw-input:focus {
  border-color: #1660ab;
  box-shadow: none;
}

.gw-input::placeholder {
  opacity: 0.5;
  font-family: "Roboto", Arial, sans-serif;
  font-size: 11px;
  color: rgba(0, 0, 0, 0.5);
}

.gw-inline-action {
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
}

.gw-inline-action .gw-input {
  flex: 1;
  min-width: 0;
}

.gw-small-btn {
  height: 36px;
  min-width: 86px;
  border: none;
  border-radius: 5px;
  background-color: #1660ab;
  color: #ffffff;
  font-family: "Roboto", Arial, sans-serif;
  font-size: 11px;
  font-weight: normal;
  cursor: pointer;
  padding: 0 10px;
  white-space: nowrap;
}

.gw-small-btn:hover:not(:disabled) {
  background: #0e5196;
}

.gw-small-btn:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

.gw-confirm-btn {
  min-width: 112px;
}

.gw-otp-status {
  min-height: 28px;
  border-radius: 5px;
  background: #fff7e8;
  color: #9a5a00;
  font-size: 11px;
  line-height: 1.35;
  display: flex;
  align-items: center;
  padding: 6px 10px;
  margin: -2px 0 12px;
}

.gw-otp-status.success {
  background: #eaf8ef;
  color: #16853a;
}

.gw-otp-status.muted {
  background: #fff7e6;
  border: 1px solid #ffd591;
  color: #ad6800;
}

.gw-password-wrap {
  position: relative;
  width: 100%;
}

.gw-input-with-icon {
  padding-right: 38px;
}

.gw-eye-btn {
  position: absolute;
  right: 9px;
  top: 50%;
  transform: translateY(-50%);
  border: 0;
  background: transparent;
  color: #8a94a6;
  cursor: pointer;
  font-size: 13px;
  line-height: 1;
  padding: 0;
}

.gw-checkbox-row {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin: 4px 0 16px;
  color: #334155;
  font-size: 11px;
  line-height: 1.45;
  cursor: pointer;
}

.gw-checkbox-row input {
  width: 16px;
  height: 16px;
  margin-top: 1px;
  accent-color: #1660ab;
  flex: 0 0 16px;
}

.gw-checkbox-row a {
  color: #1660ab;
  text-decoration: underline;
}

.gw-primary-btn {
  width: 100%;
  height: 36px;
  border: none;
  border-radius: 5px;
  background-color: #1660ab;
  font-family: "Roboto", Arial, sans-serif;
  font-size: 13px;
  font-weight: normal;
  letter-spacing: 0.3px;
  text-align: center;
  color: #ffffff;
  text-transform: uppercase;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: none;
  transition: background 0.2s ease;
}

.gw-primary-btn:hover:not(:disabled) {
  background: #0e5196;
}

.gw-primary-btn:disabled {
  opacity: 0.72;
  cursor: not-allowed;
}

.gw-btn-arrow {
  display: none;
}

.gw-primary-btn span:first-child {
  margin: 0;
  padding: 0;
}

.gw-login-link {
  text-align: center;
  margin-top: 14px;
}

.gw-login-link a {
  font-family: "Roboto", Arial, sans-serif;
  font-size: 11px;
  font-weight: normal;
  color: #1660ab;
  cursor: pointer;
  text-decoration: none;
}

.gw-login-link a:hover {
  color: #000000;
}

.gw-page-loader {
  position: fixed;
  inset: 0;
  z-index: 9999;
  background: rgba(255, 255, 255, 0.82);
  display: flex;
  align-items: center;
  justify-content: center;
}

.gw-loader-card {
  width: 260px;
  min-height: 150px;
  border-radius: 10px;
  background: #ffffff;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.12);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 24px;
  text-align: center;
}

.gw-spinner {
  width: 34px;
  height: 34px;
  border: 3px solid #d8e8f7;
  border-top-color: #1660ab;
  border-radius: 50%;
  animation: gw-spin 0.8s linear infinite;
}

.gw-loader-title {
  font-size: 14px;
  font-weight: 700;
  color: #202a3a;
}

.gw-loader-desc {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.55);
}

@keyframes gw-spin {
  to {
    transform: rotate(360deg);
  }
}

@media only screen and (max-width: 640px) {
  .gw-wrapper {
    padding: 12px;
  }

  .gw-card {
    width: 396px;
    max-width: 396px;
    padding: 38px 34px 28px;
  }

  .gw-grid {
    grid-template-columns: 1fr;
    gap: 0;
  }

  .gw-logo {
    width: 230px;
  }

  .gw-inline-action {
    flex-direction: column;
    align-items: stretch;
  }

  .gw-small-btn,
  .gw-confirm-btn {
    width: 100%;
  }
}

@media only screen and (max-width: 400px) {
  .gw-card {
    width: 300px;
    max-width: 300px;
    padding-left: 25px;
    padding-right: 25px;
  }

  .gw-logo {
    width: 220px;
  }

  .gw-title {
    font-size: 15px;
  }
}
</style>